import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { api } from 'common';
import { GoogleMapApiConfig } from '../config/GoogleMapApiConfig';
import { GoogleMapApiOptions } from '../mapConfig';
import { FONT_FAMILY } from '../common/sharedFunctions';

// Laoag; used only until the browser position or a chosen address is known.
export const DEFAULT_MAP_CENTER = { lat: 18.195992560390263, lng: 120.59268246701697 };
export const PICK_PICKUP = 'pickup';
export const PICK_DROP = 'drop';

// Builds the same address object GoogleMapsAutoComplete produces, so everything downstream
// (estimate, route, nearby drivers, the booking write) reads `description` and `coords` as usual.
const asNumber = (value) => (value === null || value === undefined || value === '' ? NaN : Number(value));

export const buildMapAddress = (coords, description) => {
  const lat = asNumber(coords ? coords.lat : null);
  const lng = asNumber(coords ? coords.lng : null);
  if (!isFinite(lat) || !isFinite(lng)) {
    return null;
  }
  const text = typeof description === 'string' && description.trim() ? description.trim() : lat.toFixed(6) + ', ' + lng.toFixed(6);
  return { description: text, coords: { lat: lat, lng: lng }, place_id: null, fromMap: true };
};

// After a pin is placed, move on to the pin that is still missing; otherwise stay.
export const nextPickMode = (mode, hasPickup, hasDrop) => {
  if (mode === PICK_PICKUP && !hasDrop) {
    return PICK_DROP;
  }
  if (mode === PICK_DROP && !hasPickup) {
    return PICK_PICKUP;
  }
  return mode;
};

const hasCoords = (address) => Boolean(address && address.coords && isFinite(Number(address.coords.lat)) && isFinite(Number(address.coords.lng)));

const containerStyle = { height: '320px', width: '100%', borderRadius: 8, fontFamily: FONT_FAMILY };
// Stable objects: the Marker re-applies its label whenever the object identity changes.
const PICKUP_LABEL = { text: 'P', color: '#ffffff', fontFamily: FONT_FAMILY };
const DROP_LABEL = { text: 'D', color: '#ffffff', fontFamily: FONT_FAMILY };
// Pin colours (change here): pickup green, drop-off red. Drawn as an SVG teardrop so the P/D label sits inside.
export const PICKUP_PIN_COLOR = '#2e7d32';
export const DROP_PIN_COLOR = '#c62828';
const PIN_PATH = 'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z';
const pinIcon = (color) => ({
  path: PIN_PATH,
  fillColor: color,
  fillOpacity: 1,
  strokeColor: '#ffffff',
  strokeWeight: 1.5,
  scale: 1.3,
  anchor: new window.google.maps.Point(0, 0),
  labelOrigin: new window.google.maps.Point(0, -29),
});
const MAP_OPTIONS = { streetViewControl: false, mapTypeControl: false, fullscreenControl: false, clickableIcons: false, disableDoubleClickZoom: true, gestureHandling: 'cooperative' };

export default function BookingMapPicker(props) {
  const { pickup, drop, onPickup, onDrop } = props;
  const { t } = useTranslation();
  const { isLoaded } = useJsApiLoader({
    id: 'google-map',
    googleMapsApiKey: GoogleMapApiConfig,
    version: GoogleMapApiOptions.v,
    libraries: GoogleMapApiOptions.libraries,
  });
  const [mode, setMode] = useState(PICK_PICKUP);
  const [browserCenter, setBrowserCenter] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [addressMissing, setAddressMissing] = useState(false);
  const [map, setMap] = useState(null);
  const requestSeq = useRef(0);
  // Built after the Maps script is loaded (needs window.google); stable across renders.
  const icons = useMemo(() => (isLoaded ? { pickup: pinIcon(PICKUP_PIN_COLOR), drop: pinIcon(DROP_PIN_COLOR) } : null), [isLoaded]);

  useEffect(() => {
    if (!navigator.geolocation) {
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => setBrowserCenter({ lat: position.coords.latitude, lng: position.coords.longitude }),
      () => {}
    );
  }, []);

  // Follow whichever address was set last (search box or pin); fall back to the browser, then Manila.
  const center = useMemo(() => {
    if (mode === PICK_DROP && hasCoords(drop)) return drop.coords;
    if (hasCoords(pickup)) return pickup.coords;
    if (hasCoords(drop)) return drop.coords;
    return browserCenter || DEFAULT_MAP_CENTER;
  }, [mode, pickup, drop, browserCenter]);

  useEffect(() => {
    if (map && center) {
      map.panTo(center);
    }
  }, [map, center]);

  // `which` says which pin this click or drag sets; a drag always sets its own pin.
  const place = useCallback(async (latLng, which) => {
    const coords = { lat: latLng.lat(), lng: latLng.lng() };
    const seq = ++requestSeq.current;
    setResolving(true);
    let description = null;
    try {
      description = await api.fetchAddressfromCoords(coords.lat + ',' + coords.lng);
    } catch (e) {
      description = null;
    }
    if (seq !== requestSeq.current) {
      return; // a newer click or drag superseded this one
    }
    setResolving(false);
    setAddressMissing(!description);
    const address = buildMapAddress(coords, description);
    if (!address) {
      return;
    }
    if (which === PICK_PICKUP) {
      onPickup(address);
    } else {
      onDrop(address);
    }
    setMode(nextPickMode(which, which === PICK_PICKUP || hasCoords(pickup), which === PICK_DROP || hasCoords(drop)));
  }, [pickup, drop, onPickup, onDrop]);

  if (!isLoaded || !icons) {
    return null;
  }

  return (
    <div>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={mode}
        onChange={(event, value) => { if (value) setMode(value); }}
        style={{ marginBottom: 8 }}
      >
        <ToggleButton value={PICK_PICKUP} style={{ fontFamily: FONT_FAMILY }}>{t('map_set_pickup')}</ToggleButton>
        <ToggleButton value={PICK_DROP} style={{ fontFamily: FONT_FAMILY }}>{t('map_set_drop')}</ToggleButton>
      </ToggleButtonGroup>
      <Typography variant="body2" style={{ fontFamily: FONT_FAMILY, marginBottom: 8 }}>
        {resolving ? t('map_locating_address') : addressMissing ? t('map_address_not_found') : t('map_pick_hint')}
      </Typography>
      <GoogleMap
        zoom={14}
        center={center}
        mapContainerStyle={containerStyle}
        options={MAP_OPTIONS}
        onLoad={setMap}
        onUnmount={() => setMap(null)}
        onClick={(e) => place(e.latLng, mode)}
      >
        {hasCoords(pickup) ?
          <Marker
            position={pickup.coords}
            draggable
            icon={icons.pickup}
            label={PICKUP_LABEL}
            title={t('pickup_location')}
            onDragEnd={(e) => place(e.latLng, PICK_PICKUP)}
          />
          : null}
        {hasCoords(drop) ?
          <Marker
            position={drop.coords}
            draggable
            icon={icons.drop}
            label={DROP_LABEL}
            title={t('drop_location')}
            onDragEnd={(e) => place(e.latLng, PICK_DROP)}
          />
          : null}
      </GoogleMap>
    </div>
  );
}